import {
  JourneyEdgeType,
  JourneyNodeStatus,
  type JourneyNode,
  type JourneyResponse,
} from './journeyFlowModel';

export const JOURNEY_CHART_HEIGHT = 459;

export function journeyNodePosition(column: number, row: number) {
  let y = row === 0 ? 36 : 82 + (row - 1) * 40;
  if (column === 0) y = 88;
  if (column === 1) y = [36, 112, 216, 250, 284, 318][row] ?? 318 + (row - 5) * 34;
  return { x: [74, 380, 700, 1010, 1320][column] ?? 74 + column * 310, y };
}

export function journeySankeyLayout(data: JourneyResponse) {
  const stages = [...data.stages].sort((a, b) => a.order - b.order);
  const measuredCount = (node: JourneyNode) =>
    node.status === JourneyNodeStatus.Observed && node.edgeType !== JourneyEdgeType.Diagnostic
      ? node.count
      : 0;
  const maximumCount = Math.max(0, ...data.nodes.map(measuredCount));
  const scale = maximumCount > 0 ? 82 / maximumCount : 0;
  const nodes = stages.flatMap((stage, column) =>
    data.nodes
      .filter((node) => node.stage === stage.id)
      .sort((a, b) => a.order - b.order)
      .map((node, row) => {
        return {
          ...node,
          ...journeyNodePosition(column, row),
          markerHeight: Math.max(3, measuredCount(node) * scale),
        };
      }),
  );
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const sourceOffsets = new Map<JourneyNode['id'], number>();
  const links = nodes.flatMap((target) => {
    const source = target.parentId ? byId.get(target.parentId) : undefined;
    if (!source) return [];
    const measuredFlow =
      target.edgeType === JourneyEdgeType.Flow &&
      source.status === JourneyNodeStatus.Observed &&
      target.status === JourneyNodeStatus.Observed &&
      source.unit === target.unit &&
      target.count <= source.count;
    const value = measuredFlow ? target.count : 0;
    const width = value * scale;
    const offset = sourceOffsets.get(source.id) ?? 0;
    sourceOffsets.set(source.id, offset + width);
    const x0 = source.x + 7;
    const x1 = target.x;
    const middle = (x0 + x1) / 2;
    const y0 = source.y + offset + width / 2;
    const y1 = target.y + width / 2;
    // Unit changes connect the actual node widths, without implying a parent share.
    const transitionBand =
      target.edgeType === JourneyEdgeType.UnitTransition &&
      measuredCount(source) > 0 &&
      measuredCount(target) > 0;
    const sourceWidth = Math.max(1, measuredCount(source) * scale);
    const targetWidth = Math.max(1, measuredCount(target) * scale);
    const path = transitionBand
      ? `M${String(x0)},${String(source.y)}C${String(middle)},${String(source.y)},${String(middle)},${String(target.y)},${String(x1)},${String(target.y)}L${String(x1)},${String(target.y + targetWidth)}C${String(middle)},${String(target.y + targetWidth)},${String(middle)},${String(source.y + sourceWidth)},${String(x0)},${String(source.y + sourceWidth)}Z`
      : `M${String(x0)},${String(y0)}C${String(middle)},${String(y0)},${String(middle)},${String(y1)},${String(x1)},${String(y1)}`;
    return [
      {
        sourceId: source.id,
        targetId: target.id,
        edgeType: target.edgeType,
        value,
        width,
        transitionBand,
        path,
      },
    ];
  });
  return { nodes, links };
}
